import { useEffect, useRef, useState, useCallback } from 'react';
import {
  createActionSoundTracker, createWinSoundTracker, createTurnSoundTracker, winnerKey, preloadActionSounds, bindAudioUnlock,
  playActionSound, isSoundMuted, setSoundMuted,
} from './actionSounds';

/** Plays the table sound for each new synced lastAction. */
export function useActionSounds(lastAction, muted) {
  const trackerRef = useRef(null);
  if (!trackerRef.current) trackerRef.current = createActionSoundTracker();

  useEffect(() => {
    preloadActionSounds();
    bindAudioUnlock();
  }, []);

  const key = lastAction ? `${lastAction.ts}|${lastAction.playerId}|${lastAction.kind}` : '';
  useEffect(() => {
    const name = trackerRef.current(lastAction);
    if (name) playActionSound(name, { muted });
    // key captures every field the tracker reads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/** Win chime on every phone, once per awarded pot. */
export function useWinSound(winnerInfo, muted) {
  const t = useRef(null);
  if (!t.current) t.current = createWinSoundTracker();
  const key = winnerKey(winnerInfo) || '';
  useEffect(() => {
    const name = t.current(winnerInfo);
    if (name) playActionSound(name, { muted });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/** Your-turn chime, only on the phone whose turn it just became. */
export function useTurnSound({ turnId, myId, handNumber, stage }, muted) {
  const t = useRef(null);
  if (!t.current) t.current = createTurnSoundTracker();
  useEffect(() => {
    const name = t.current({ turnId, myId, handNumber, stage });
    if (name) playActionSound(name, { muted });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnId, myId, handNumber, stage]);
}

/** Mute state, persisted in localStorage on this phone. */
export function useSoundMuted() {
  const [muted, setMuted] = useState(() => isSoundMuted());
  const toggle = useCallback(() => {
    setMuted((m) => {
      setSoundMuted(!m);
      return !m;
    });
  }, []);
  return [muted, toggle];
}
